/**
 * Small helpers for the Mulch Calculator's JSON export/import flow.
 * Kept in one place so the download/upload UX stays consistent across
 * the editor header, the Location detail page, and the Locations directory.
 */

/**
 * Trigger a browser download of a JSON payload. `filename` should include
 * the .json extension. Uses the standard blob + anchor pattern \u2014 no libs.
 */
export function downloadJson(filename: string, payload: any): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the browser has time to kick off the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Build a safe filename fragment from a Map or Location name. Strips
 * anything not alphanumeric / dash / underscore. Empty results fall back
 * to the provided default.
 */
export function safeFilename(name: string | null | undefined, fallback = "map"): string {
  const cleaned = (name ?? "").trim().replace(/[^A-Za-z0-9-_]/g, "_");
  return cleaned || fallback;
}

/**
 * Prompt the user to select a JSON file and return its parsed contents.
 * Returns null if the user cancels or the file isn't valid JSON.
 */
export function pickJsonFile(): Promise<any | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        resolve(parsed);
      } catch {
        resolve(null);
      }
    };
    // Fires when the user cancels the picker in most browsers via the
    // 'cancel' event; not universal but harmless when it isn't supported.
    input.oncancel = () => resolve(null);
    input.click();
  });
}
